import { isAxiosError } from 'axios';
import { SavedCart } from 'services/savedCartApi';
import { getRecipientsAPI } from 'store/recipients/actions';
import { IRecipient, RecipientsRequestType } from 'store/recipients/types';
import { IShoppingCartSearchConfig } from 'store/shopping_cart/types';

export type CartRestoreMode = 'logged_in' | 'guest_link' | 'guest';
export type CartRestorePhase = 'cart' | 'recipients';
export type FailReason =
  | 'deadline'
  | 'error'
  | 'config_error'
  | 'no_cart_page'
  | 'not_found'
  | 'closed';

export class RestoreFailure extends Error {
  constructor(
    readonly reason: FailReason,
    readonly status?: unknown,
    readonly isTransient = false
  ) {
    super(reason);
  }
}

export const errorStatus = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') return undefined;
  // RTK Query errors carry `status`; axios errors carry `response.status`.
  if ('response' in error) return (error as { response?: { status?: number } }).response?.status;
  if ('status' in error) return error.status;
  return undefined;
};

/**
 * Network failures, timeouts and server errors: the donor's connection, or a failure the API logs
 * itself. Logged below error, since every site error log alerts.
 */
export const isTransientFailure = (error: unknown): boolean => {
  const status = errorStatus(error);
  if (typeof status === 'number') return status >= 500;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
  // axios reports a network failure as an error with no response.
  return isAxiosError(error) && !error.response;
};

export type Unwrappable<T> = { unwrap: () => Promise<T> };

export interface LoadSavedCartArgs {
  cartRestoreMode: CartRestoreMode;
  clientId: number;
  donorCartParam?: string;
  getJwt: () => Promise<string | undefined>;
  onJwtRejected: () => void;
  getCurrentCart: (args: { client_id: number; jwt?: string }) => Unwrappable<SavedCart | null>;
  getCart: (args: { id: string }) => Unwrappable<SavedCart>;
  getClientCartConfig: () => Promise<IShoppingCartSearchConfig>;
  /** True once the caller stopped waiting (deadline or unmount): no further requests. */
  isCancelled: () => boolean;
  setCartRestorePhase: (phase: CartRestorePhase) => void;
}

export type LoadedCart =
  | { cart: SavedCart | null } // no cart, or an empty one: nothing more to load
  | { cart: SavedCart; recipients: IRecipient[]; config: IShoppingCartSearchConfig };

const fetchCurrentCart = async ({
  clientId,
  getJwt,
  onJwtRejected,
  getCurrentCart,
}: LoadSavedCartArgs) => {
  const request = async (jwt?: string) => {
    try {
      return await getCurrentCart({ client_id: clientId, jwt }).unwrap();
    } catch (error) {
      // /current 404s only for a client with no cart page.
      if (errorStatus(error) === 404) throw new RestoreFailure('no_cart_page', 404);
      throw error;
    }
  };

  const jwt = await getJwt();
  try {
    return await request(jwt);
  } catch (error) {
    // An expired JWT is rejected before the route runs; the guest token still finds this
    // browser's cart.
    if (jwt && errorStatus(error) === 401) {
      onJwtRejected();
      return request();
    }
    throw error;
  }
};

const fetchLinkedCart = async ({ donorCartParam, getCart }: LoadSavedCartArgs) => {
  let cart: SavedCart;
  try {
    cart = await getCart({ id: donorCartParam as string }).unwrap();
  } catch (error) {
    if (errorStatus(error) === 404) throw new RestoreFailure('not_found', 404);
    throw error;
  }
  if (cart.status !== 'OPEN') throw new RestoreFailure('closed');
  return cart;
};

/**
 * Finds the donor's saved cart for this page and its recipients. Throws `RestoreFailure` for
 * known outcomes; resolves `undefined` if cancelled part way.
 */
export async function loadSavedCart(args: LoadSavedCartArgs): Promise<LoadedCart | undefined> {
  const cart =
    args.cartRestoreMode === 'guest_link'
      ? await fetchLinkedCart(args)
      : await fetchCurrentCart(args);
  if (args.isCancelled()) return undefined;
  if (!cart || cart.recipient_ids.length === 0) return { cart };

  args.setCartRestorePhase('recipients');
  let config: IShoppingCartSearchConfig;
  try {
    config = await args.getClientCartConfig();
  } catch (error) {
    throw new RestoreFailure('config_error', errorStatus(error), isTransientFailure(error));
  }
  if (args.isCancelled()) return undefined;

  const response = await getRecipientsAPI({
    // amount is the result limit, so ask for the whole cart.
    amount: cart.recipient_ids.length,
    recipient_ids: cart.recipient_ids,
    order_by: 'custom',
    // Defaults to DE_OATH server-side, which would drop Stripe-only recipients.
    payment_processor: config.payment_processor,
    request_type: RecipientsRequestType.donor_search,
    request_location_path: window.location.pathname,
  });
  if (args.isCancelled()) return undefined;

  return { cart, recipients: response.data.recipients ?? [], config };
}
